import { useCallback, useEffect, useRef } from "react";
import { AccountInfo } from "@azure/msal-browser";
import { msalInstance, loginRequest, graphOrgRequest } from "../../config/msalConfig";
import AppApi from "../../api/AppApi";
import { SsoMember } from "../../generated/dashboard-client";

interface UseMicrosoftAuthProps {
  onSuccess: (member: SsoMember, orgName?: string, returnPath?: string) => void;
  onStartLoading: () => void;
  onError?: (message: string) => void;
}

// Reads the tenant's organization display name via Microsoft Graph. Best-effort: if the app hasn't
// been granted admin consent for Organization.Read.All in this tenant, the silent token request
// throws and we return undefined so the caller can fall back to the email-domain label.
async function fetchMicrosoftOrgName(account: AccountInfo | null): Promise<string | undefined> {
  if (!account) return undefined;
  try {
    const tokenResp = await msalInstance.acquireTokenSilent({ ...graphOrgRequest, account });
    const res = await fetch("https://graph.microsoft.com/v1.0/organization?$select=displayName", {
      headers: { Authorization: `Bearer ${tokenResp.accessToken}` },
    });
    if (!res.ok) return undefined;
    const data = await res.json();
    return data?.value?.[0]?.displayName || undefined;
  } catch (e) {
    console.warn("Could not retrieve Microsoft organization name:", e);
    return undefined;
  }
}

// Module-level (shared across every hook instance for this page load) so the redirect response is
// handled exactly once. The per-instance `processed` ref isn't enough: navigating between steps (e.g.
// Back from the About step) mounts a fresh hook instance whose handleRedirectPromise returns MSAL's
// cached result again — re-firing onStartLoading + onSuccess. For an owner that hits
// onMicrosoftInviteSuccess's `if (!inviteId) return` and leaves loading stuck (spinning). A real new
// login is a full-page redirect, which reloads the module and resets this.
let microsoftRedirectConsumed = false;

export function useMicrosoftAuth({ onSuccess, onStartLoading, onError }: UseMicrosoftAuthProps) {
  const processed = useRef(false);
  const initPromise = useRef<Promise<void> | null>(null);

  const ensureInitialized = useCallback(() => {
    if (!initPromise.current) {
      initPromise.current = msalInstance.initialize();
    }
    return initPromise.current;
  }, []);

  const handleRedirectResult = useCallback(async () => {
    try {
      await ensureInitialized();
      if (processed.current || microsoftRedirectConsumed) return;
      processed.current = true;
      // Call handleRedirectPromise unconditionally — do NOT gate on `code=` in the URL. With MSAL's
      // navigateToLoginRequestUrl=true (default), the redirect response is consumed on the redirectUri
      // page (/account/login) and the user is navigated back to the ORIGINAL url (e.g. an invite at
      // /account/register/:inviteId) which no longer contains `code=`. The cached response is only
      // delivered when handleRedirectPromise runs here; gating on `code=` skipped it and the invite
      // flow silently did nothing. handleRedirectPromise resolves null when there's no redirect.
      const result = await msalInstance.handleRedirectPromise();
      if (result?.idToken) {
        microsoftRedirectConsumed = true;
        onStartLoading();
        const resp = await AppApi.getMicrosoftMember(result.idToken);
        const orgName = await fetchMicrosoftOrgName(result.account);
        // result.state is the originating path we set in loginWithMicrosoft (e.g. an invite URL).
        onSuccess(resp.data, orgName, result.state ?? undefined);
      }
    } catch (e: any) {
      processed.current = false;
      const code = e?.response?.data?.message || "";
      onError?.(
        code === "org_not_enabled"
          ? "Your organization isn't enabled for Microsoft sign-in. Please contact your administrator."
          : "We couldn't sign you in with Microsoft. Please try again."
      );
      console.error("Microsoft auth error:", e);
    }
  }, [ensureInitialized, onSuccess, onStartLoading, onError]);

  useEffect(() => {
    handleRedirectResult();
  }, [handleRedirectResult]);

  const loginWithMicrosoft = useCallback(async (returnPath?: string) => {
    try {
      await ensureInitialized();
      // We always land back on the static, Azure-registered /account/login. Carry the originating
      // location in `state` so the handler there knows what to do: an invite path
      // (/account/register/:inviteId), a drd path (/account/register?source=drd&community=...), or a
      // plain owner sign-in. Defaults to the current pathname.
      await msalInstance.loginRedirect({ ...loginRequest, state: returnPath || window.location.pathname });
    } catch (e) {
      console.error("Microsoft login error:", e);
    }
  }, [ensureInitialized]);

  return { loginWithMicrosoft };
}
